import React from 'react';
import { Box, Stat, StatLabel, StatNumber, StatHelpText, Tooltip, Icon } from '@chakra-ui/react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { ArrowUpIcon, ArrowDownIcon, MinusIcon, InfoOutlineIcon } from '@chakra-ui/icons';

const trendMeta = (trend) => {
  if (trend === 'up') return { icon: <ArrowUpIcon />, text: 'Up', color: 'status.pass' };
  if (trend === 'down') return { icon: <ArrowDownIcon />, text: 'Down', color: 'status.fail' };
  return { icon: <MinusIcon />, text: 'No change', color: 'gray.400' };
};

const Sparkline = ({ data, color = '#14b36e' }) => (
  <Box w="100%" h="40px">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={data} margin={{ top: 6, right: 4, bottom: 0, left: 0 }}>
        <Line type="monotone" dataKey="value" stroke={color} strokeWidth={2} dot={false} />
      </LineChart>
    </ResponsiveContainer>
  </Box>
);

/**
 * Flat stat tile per the design language: white surface, hairline border,
 * small label, large number, a trend chip, optional sparkline or children.
 */
const SummaryCard = ({ label, value, trend = 'neutral', tooltipText, children, sparklineData, sparklineColor, compact = false }) => {
  const t = trendMeta(trend);
  return (
    <Box
      bg="white"
      borderRadius="14px"
      borderWidth="1px"
      borderColor="gray.200"
      p={compact ? 3 : 4}
      minW={compact ? '120px' : { base: '140px', md: '170px' }}
      transition="border-color 0.15s"
      _hover={{ borderColor: 'gray.300' }}
      role="group"
    >
      <Stat>
        <StatLabel fontWeight="500" color="gray.500" fontSize={compact ? 'xs' : 'sm'}>
          <Tooltip label={tooltipText} hasArrow placement="top" isDisabled={!tooltipText}>
            <Box as="span" display="inline-flex" alignItems="center" gap={1}>
              {label}
              {tooltipText && <Icon as={InfoOutlineIcon} color="gray.300" _groupHover={{ color: 'gray.400' }} />}
            </Box>
          </Tooltip>
        </StatLabel>
        <StatNumber fontSize={compact ? 'xl' : '28px'} fontWeight="800" letterSpacing="-0.5px" color="gray.900" mt={1}>
          {value}
        </StatNumber>
        {children ? (
          <Box mt={1}>{children}</Box>
        ) : sparklineData ? (
          <Box mt={1}>
            <Sparkline data={sparklineData} color={sparklineColor} />
          </Box>
        ) : null}
        <StatHelpText fontSize="xs" color={t.color} display="inline-flex" alignItems="center" gap={1} mb={0} mt={1.5}>
          {t.icon} {t.text}
        </StatHelpText>
      </Stat>
    </Box>
  );
};

export default SummaryCard;
